'use client';

/**
 * CarouselTabsSection — 상품 탭(HeaderTabs) + 탭마다 슬라이드(rolling | pagination | navigation)
 *
 * 연관 파일 (같은 디렉토리):
 *  - types.ts — 공통 타입
 *  - RollingSlides.tsx — variant='rolling' 슬라이드 (ContentMarquee 기반)
 *  - StandardSlides.tsx — variant='pagination' | 'navigation' 슬라이드 (Carousel 기반)
 *  - TabSlide.tsx — 개별 슬라이드 (tab.title 유무로 CardSlideFlex ↔ SlideImage 분기)
 *  - SlideImage.tsx — 반응형 이미지 + caption
 * 외부:
 *  - HeaderTabs.tsx — 타이틀이 결합된 상품 탭 UI
 *
 *  - `title`, `tabs`만 넣어도 기본 pagination UI가 렌더링됩니다.
 *  - `tabs[]`: `label`, `images`만 있으면 이미지 슬라이드
 *  - `tabs[].title`: 있으면 이미지 + 제목/설명/배지가 있는 content 슬라이드
 *  - `tabs[].images`: 탭에 들어갈 이미지 목록. 각 이미지는 `PC` 또는 `[PC, MO?]`
 *  - `variant`: UI 종류. 생략하면 `'pagination'`
 *      - `'rolling'`: 자동으로 흐르는 이미지
 *      - `'pagination'`: 페이지네이션이 있는 슬라이드
 *      - `'navigation'`: 화살표 중심 슬라이드
 *  - `tabWidth`: 탭 버튼 폭 `170`(기본) | `112`. `[pc, mo]` 분기 가능 — 112: 한 줄 3개 / 170: 한 줄 2개 (가로형 PC·모바일)
 *  - `tabLine`: 탭 버튼 테두리 on/off. 생략 시 `theme`이 primary면 on, secondary면 off
 *  - `data`: 탭 블록 데이터. 여러 개면 배열로 전달
 *  - `subTitle`, `title`, `desc` — 섹션 타이틀 영역
 *  - `subText`, `button` — direction=`'column'`(기본) 데이터에서만 사용
 *  - 탭 하단 문구: 모든 탭이 같으면 `subText`, 탭마다 다르면 `tabs[].subText` (자리·스타일 동일)
 *
 * [슬라이드 UI — 조합 매트릭스]
 *  표현 가능한 슬라이드 모양 = ① 캐러셀 컨트롤(`variant`, 3종) × ② 콘텐츠 타입(2종) = 6가지.
 *  두 축은 서로 독립이며, 여기에 `direction`·`theme`·`tabWidth`·`tabLine`이 레이아웃·스킨으로 더해진다.
 *
 *  ① 컨트롤 = `variant`  (이미지 1장이면 셋 다 컨트롤 없이 정적 렌더)
 *      - `'rolling'`    : 자동 흐름 마퀴(이미지 2장+)          → RollingSlides / ContentMarquee
 *      - `'pagination'` : 화살표 + 하단 페이지네이션 점         → StandardSlides / Carousel
 *      - `'navigation'` : 화살표만                              → StandardSlides / Carousel
 *  ② 콘텐츠 = `tab.title` 유무  (variant와 독립, 탭 단위로 결정 — 한 탭의 모든 이미지가 공유)
 *      - title 없음 → 이미지 슬라이드: `images` (+ `caption` 하단 오버레이)  → SlideImage
 *      - title 있음 → 카드 슬라이드:   `title`/`text`/`badges`/`note`        → CardSlideFlex
 *
 *  제약:
 *      - `caption`(이미지)과 `title`(카드)은 동시 사용 불가 — 타입으로 강제.
 *      - `slideTextTop`/`slideTextBottom`은 `pagination`·`navigation`에서만 표시(rolling은 무시).
 *
 * [Props — page.tsx]
 *  - `data`: CarouselTabsData | CarouselTabsData[]
 *  - `direction`: `'column'`(기본) | `'row'`  ※ row data에는 subText·button을 넣을 수 없음
 *  - `theme`: `'primary'`(기본) | `'secondary'`
 *
 * [사용 예]
 * ```tsx
 * // page.tsx
 * import myImg from '@publishing/assets/.../img.jpg';
 *
 * <CarouselTabsSection
 *   data={{
 *     title: '섹션 타이틀',
 *     tabs: [
 *       { label: '탭1', caption: '탭 하단 캡션', images: [myImg] },
 *       { label: '탭2', title: '카드 제목', text: '카드 설명', badges: ['NEW'], images: [myImg] },
 *     ],
 *   }}
 * />
 * ```
 */

import clsx from 'clsx';
import { useRef } from 'react';
import { CommonInner } from '@publishing/components/ui/CommonInner';
import HeaderTabs from '@publishing/modules/HeaderTabs';
import type {
  CarouselTabsData,
  CarouselTabsRowData,
  CarouselTabsTheme,
} from './types';
import { RollingSlides } from './RollingSlides';
import { StandardSlides } from './StandardSlides';
import { useEqualMobileCardHeight } from './useEqualMobileCardHeight';
import s from './CarouselTabsSection.module.scss';

export type { CarouselTabsData, CarouselTabsRowData } from './types';

type CarouselTabsColumnProps = {
  direction?: 'column';
  theme?: CarouselTabsTheme;
  index?: number;
  data: CarouselTabsData | CarouselTabsData[];
};

type CarouselTabsRowProps = {
  direction: 'row';
  theme?: CarouselTabsTheme;
  index?: number;
  data: CarouselTabsRowData | CarouselTabsRowData[];
};

type CarouselTabsSectionProps = CarouselTabsColumnProps | CarouselTabsRowProps;

export default function CarouselTabsSection(props: CarouselTabsSectionProps) {
  const { data, direction = 'column', theme = 'primary', index = 0 } = props;
  const dataList = Array.isArray(data) ? data : [data];
  const sectionRef = useRef<HTMLElement>(null);

  useEqualMobileCardHeight(sectionRef);

  return (
    <section
      ref={sectionRef}
      id={dataList.find((item) => item.id)?.id}
      className={clsx(s.carousel_tabs, s[`carousel_tabs_theme_${theme}`])}
    >
      <CommonInner className={s.carousel_tabs_inner}>
        {dataList.map((tabsData, dataIndex) => {
          const {
            tabs: tabItems,
            variant = 'pagination',
            tabWidth,
            tabLine,
            subTitle,
            title,
            desc,
            button,
            subText,
          } = tabsData;
          const tabs = tabItems.map((tab) => ({ text: tab.label }));
          const headerTabsLayoutProps =
            direction === 'row'
              ? { direction: 'row' as const }
              : {
                  direction: 'column' as const,
                  button,
                  subText: subText ?? tabItems.map((tab) => tab.subText),
                };

          return (
            <HeaderTabs
              key={`${variant}-${dataIndex}`}
              className={
                variant === 'rolling' ? s.carousel_tabs_rolling : undefined
              }
              tabWidth={tabWidth}
              tabLine={tabLine ?? theme === 'primary'}
              subTitle={subTitle}
              title={title}
              desc={desc}
              tabs={tabs}
              index={index}
              {...headerTabsLayoutProps}
              renderAllPanels
              renderPanel={(_, activeIndex) => {
                const tab = tabItems[activeIndex];
                if (variant === 'rolling') {
                  return (
                    <RollingSlides
                      tab={tab}
                      direction={direction}
                      theme={theme}
                    />
                  );
                }

                return (
                  <StandardSlides tab={tab} variant={variant} theme={theme} />
                );
              }}
            />
          );
        })}
      </CommonInner>
    </section>
  );
}
